import React, { useRef, useEffect, useState, useMemo } from "react";
import { useLaunchParams } from "@telegram-apps/sdk-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  PositionData,
  ClosedPositionData,
} from "@crypto-trading/common/browser";
import { Loader2 } from "lucide-react";
import { Drawer } from "vaul";
import { PositionChart } from "./PositionChart";
import {
  TradingViewChartWidget,
} from "@crypto-trading/ui-shared/client";
import {
  usePositionPriceData,
} from "@/hooks/usePositionPriceData";
import { ApiClientManager } from "@/utils/api-client-factory";
import { PnlCardShare } from "./PnlCardShare";
import { useReferralStats } from "@/hooks/useReferrals";
import { exchangeKeys } from "@/hooks/useExchanges";
import {
  useTokenLivePrice,
  LivePriceExchangeCode,
} from "@/hooks/useTokenLivePrice";
import { trackEvent, type PositionDrawerSource } from "@/utils/analytics";
import { getTradeErrorMessage } from "@/utils/trade-error";

interface PositionDetailDrawerProps {
  userId: string;
  position: PositionData | ClosedPositionData | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPositionUpdate?: (
    updatedPosition: PositionData | ClosedPositionData
  ) => void;
  nested?: boolean; // If true, use Drawer.NestedRoot instead of Drawer.Root
  hideCloseAction?: boolean;
  source?: PositionDrawerSource;
}

const isClosedPosition = (
  pos: PositionData | ClosedPositionData
): pos is ClosedPositionData => {
  return (
    "closedAt" in pos && pos.closedAt !== null && pos.closedAt !== undefined
  );
};

/**
 * Map exchange code/name to live price exchange code
 */
const mapExchangeCode = (
  exchangeCode?: string,
  exchangeName?: string
): LivePriceExchangeCode => {
  const code = (exchangeCode || exchangeName || "").toLowerCase();
  if (code.includes("binance")) return "binance";
  if (code.includes("bybit")) return "bybit";
  if (code.includes("gate")) return "gate";
  if (code.includes("kucoin")) return "kucoin";
  if (code.includes("aster")) return "asterdex";
  return "binance"; // Default
};

/**
 * Calculate unrealized PnL based on position data
 */
const calculateUnrealizedPnL = (
  entryPrice: number,
  markPrice: number,
  usdAmount: number,
  leverage: number,
  positionType: string
): { pnl: number; pnlPercentage: number } => {
  if (!entryPrice || !markPrice || entryPrice === 0) {
    return { pnl: 0, pnlPercentage: 0 };
  }

  const isLong = positionType.toUpperCase() === "LONG";
  const priceChange = isLong
    ? (markPrice - entryPrice) / entryPrice
    : (entryPrice - markPrice) / entryPrice;

  const pnl = usdAmount * leverage * priceChange;
  const pnlPercentage = priceChange * leverage * 100;

  return { pnl, pnlPercentage };
};

export const PositionDetailDrawer: React.FC<PositionDetailDrawerProps> = ({
  userId,
  position,
  open,
  onOpenChange,
  onPositionUpdate,
  nested = false,
  hideCloseAction = false,
  source = "positions_page",
}) => {
  const launchParams = useLaunchParams();
  const telegramUser = launchParams.initData?.user;
  const userDisplayName =
    (telegramUser?.username && `@${telegramUser.username}`) ||
    [telegramUser?.firstName, telegramUser?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    "Trigger Trader";
  const { data: referralStats } = useReferralStats(userId);
  const referralLink = referralStats?.referralLink;

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<number>(0);
  const preserveCloseSuccess = useRef(false);
  const [isLiveChart, setIsLiveChart] = useState(false);
  const [closeSuccess, setCloseSuccess] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);

  const closed = position ? isClosedPosition(position) : false;
  const queryClient = useQueryClient();
  
  // Check if position exists on exchange (only relevant for open positions)
  const existsOnExchange = closed ? true : (position as PositionData)?.existsOnExchange ?? true;
  const isClosedExternally = !closed && !existsOnExchange;

  // Get exchange code for live price subscription
  const exchangeCode = useMemo(
    () =>
      position
        ? mapExchangeCode(position.exchangeCode, position.exchangeName)
        : "binance",
    [position?.exchangeCode, position?.exchangeName]
  );

  // Subscribe to live price updates when drawer is open (only for open positions that exist on exchange)
  const { price: liveMarkPrice, isSubscribed } = useTokenLivePrice(
    position?.symbol || "",
    exchangeCode,
    open && !closed && existsOnExchange && !!position // Only subscribe for open positions that exist on exchange when drawer is open
  );

  // Parse position values
  const entryPrice = position
    ? typeof position.entryPrice === "string"
      ? parseFloat(position.entryPrice)
      : position.entryPrice
    : 0;
  const storedMarkPrice =
    position && !closed && "markPrice" in position
      ? typeof position.markPrice === "string"
        ? parseFloat(position.markPrice)
        : (position.markPrice ?? 0)
      : 0;
  const usdAmount = position
    ? typeof position.usdAmount === "string"
      ? parseFloat(position.usdAmount)
      : position.usdAmount
    : 0;
  const leverage = position
    ? typeof position.leverage === "string"
      ? parseFloat(position.leverage)
      : position.leverage
    : 0;

  // Use live mark price if available, otherwise use stored mark price
  const displayMarkPrice = liveMarkPrice ?? storedMarkPrice;

  // Calculate live PnL for open positions
  const livePnL = useMemo(() => {
    if (!position || closed || !displayMarkPrice) return null;
    return calculateUnrealizedPnL(
      entryPrice,
      displayMarkPrice,
      usdAmount,
      leverage,
      position.positionType
    );
  }, [position, closed, displayMarkPrice, entryPrice, usdAmount, leverage]);

  // Create a derived position with live PnL values for the PnL card
  const positionForCard = useMemo(():
    | PositionData
    | ClosedPositionData
    | null => {
    if (!position) return null;
    if (closed || !livePnL) return position;

    // For open positions, merge live PnL values
    const openPosition = position as PositionData;
    return {
      ...openPosition,
      pnl: livePnL.pnl.toString(),
      pnlPercentage: livePnL.pnlPercentage.toString(),
      markPrice: displayMarkPrice.toString(),
    };
  }, [position, closed, livePnL, displayMarkPrice]);

  const closePositionMutation = useMutation<ClosedPositionData | null, Error>({
    mutationFn: async () => {
      if (!position || closed || !("positionId" in position)) {
        throw new Error("Position is already closed");
      }

      const client = ApiClientManager.getPositionClient();
      await client.closePosition({
        userId,
        symbol: position.symbol,
        positionId: position.positionId,
      });

      const closedPositions = await client.getClosedPositions(userId, 50, 0);
      return (
        closedPositions.positions.find(
          (closedPosition) => closedPosition.symbol === position.symbol
        ) || null
      );
    },
    onSuccess: async (updatedPosition) => {
      setCloseError(null);
      preserveCloseSuccess.current = true;
      setCloseSuccess(true);
      setIsLiveChart(false);
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({ top: 0, behavior: "smooth" });
      }
      const invalidatePromises: Promise<unknown>[] = [
        queryClient.invalidateQueries({
          queryKey: ["positions", "open"],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: ["positions", "closed"],
          exact: false,
        }),
      ];

      if (userId) {
        invalidatePromises.push(
          queryClient.invalidateQueries({
            queryKey: exchangeKeys.userExchanges(userId, true),
          }),
          queryClient.invalidateQueries({
            queryKey: exchangeKeys.userExchanges(userId, false),
          }),
          queryClient.invalidateQueries({
            queryKey: exchangeKeys.activeExchange(userId),
          })
        );
      }

      await Promise.all(invalidatePromises);
      if (updatedPosition && onPositionUpdate) {
        onPositionUpdate(updatedPosition);
      }
    },
    onError: (error) => {
      setCloseError(getTradeErrorMessage(error, "Failed to close position"));
    },
  });

  const positionIdentifier = position
    ? "positionId" in position
      ? position.positionId
      : position.id
    : null;

  useEffect(() => {
    if (preserveCloseSuccess.current) {
      preserveCloseSuccess.current = false;
      return;
    }
    setCloseSuccess(false);
    setCloseError(null);
  }, [positionIdentifier]);

  useEffect(() => {
    if (!open || !position) return;
    const positionId =
      "positionId" in position ? position.positionId : position.id;
    trackEvent("position_drawer_view", {
      userId,
      positionId,
      symbol: position.symbol,
      positionType: position.positionType as "LONG" | "SHORT",
      isClosed: closed,
      source,
    });
  }, [open, position, closed, source]);

  // Reset live chart toggle when drawer closes
  useEffect(() => {
    if (!open) {
      setIsLiveChart(false);
    }
  }, [open]);

  const {
    enabledIntervals,
    selectedInterval,
    setSelectedInterval,
    priceChanges,
    isLoading,
    isLoadingMore,
    error: candleError,
    loadOlderCandles,
    loadNewerCandles,
    hasMoreHistory,
    hasMoreFuture,
    setFollowLive,
  } = usePositionPriceData({
    userId,
    position,
    open,
  });

  // Restore scroll position after interval/data changes
  useEffect(() => {
    if (scrollContainerRef.current && scrollPositionRef.current > 0) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollPositionRef.current;
        }
      });
    }
  }, [selectedInterval, isLoading, priceChanges.length]);

  if (!position || !open) return null;
  const isAutotraded =
    closed &&
    "tradeOrigin" in position &&
    position.tradeOrigin === "AUTO_TRADE";
  const isCopyTraded =
    closed &&
    "tradeOrigin" in position &&
    position.tradeOrigin === "COPY_TRADE";

  const formatPrice = (price: number | string | null | undefined) => {
    if (!price) return "N/A";
    const numPrice = typeof price === "string" ? parseFloat(price) : price;
    return `$${numPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })}`;
  };

  const formatAmount = (amount: number | string) => {
    const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;
    return `$${numAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDateTime = (date: string | null | undefined) => {
    if (!date) return "N/A";
    const d = new Date(date);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h24",
    });
  };

  // Use live PnL for open positions, realized PnL for closed positions
  const pnl =
    closed && "realizedPnl" in position
      ? position.realizedPnl
      : (livePnL?.pnl ?? parseFloat((position as PositionData).pnl || "0"));
  const pnlPercentage =
    closed && "pnlPercentage" in position
      ? position.pnlPercentage
      : (livePnL?.pnlPercentage ??
        parseFloat((position as PositionData).pnlPercentage || "0"));
  const isProfitable = pnl !== null && pnl !== undefined && pnl >= 0;
  const showCloseAction =
    (!closed || closeSuccess || !!closeError) && !hideCloseAction;
  const isCloseButtonDisabled = closePositionMutation.isPending;

  const DrawerRoot = nested ? Drawer.NestedRoot : Drawer.Root;

  return (
    <DrawerRoot open={open} onOpenChange={onOpenChange}>
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 bg-black/80 z-[100]" />
        <Drawer.Content className="bg-secondary flex rounded-t-xl flex-col fixed bottom-0 left-0 right-0 max-h-[90vh] z-[101]">
          {/* Header */}
          <div className="mx-4 border-b border-gray-500/30 pb-3 pt-2 sticky top-0 bg-secondary z-10">
            <Drawer.Handle className="h-1 w-1/3 mt-0 mb-3 rounded-full" />
            <h2 className="text-xs text-text-primary text-center">
              Position Details
            </h2>
            <div className="flex items-center gap-2 mt-3">
              <h2 className="text-md font-bold text-text-primary">
                {position.symbol}
              </h2>
              <span className={`px-2 py-0.5 rounded-full text-xs  bg-black/50`}>
                {position.positionType}
              </span>
              {isAutotraded && (
                <span
                  className={`px-2 py-0.5 rounded-full text-xs bg-accent/10`}
                >
                  Autotraded 🤖
                </span>
              )}
              {isCopyTraded && (
                <span className="px-2 py-0.5 rounded-full text-xs bg-accent/10">
                  Copy traded 📋
                </span>
              )}
            </div>
          </div>
          <div
            ref={scrollContainerRef}
            className="flex-1 overflow-y-auto space-y-4"
          >
            {/* Content */}
            <div className={`p-4 space-y-6 ${isClosedExternally ? 'opacity-50' : ''}`}>
              {/* Position Info Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-text-secondary mb-1">Amount</p>
                  <p className="text-sm text-text-primary">
                    {formatAmount(position.usdAmount)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-text-secondary mb-1">Leverage</p>
                  <p className="text-sm  text-text-primary">
                    {position.leverage}x
                  </p>
                </div>
                <div>
                  <p className="text-xs text-text-secondary mb-1">
                    Entry Price
                  </p>
                  <p className="text-sm  text-text-primary">
                    {formatPrice(position.entryPrice)}
                  </p>
                </div>
                {closed && (
                  <div>
                    <p className="text-xs text-text-secondary mb-1">
                      Exit Price
                    </p>
                    <p className="text-sm  text-text-primary">
                      {isClosedExternally ? "---" : formatPrice(position.exitPrice)}
                    </p>
                  </div>
                )}
                {!closed && (
                  <div>
                    <div className="flex items-center gap-1 mb-1">
                      <p className="text-xs text-text-secondary">Mark Price</p>
                      {isSubscribed && liveMarkPrice && (
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                        </span>
                      )}
                    </div>
                    <p
                      className={`text-sm text-text-primary ${isSubscribed && liveMarkPrice ? "transition-all duration-150" : ""}`}
                    >
                      {isClosedExternally ? "---" : formatPrice(displayMarkPrice)}
                    </p>
                  </div>
                )}
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    <p className="text-xs text-text-secondary">PNL</p>
                    {!closed && isSubscribed && liveMarkPrice && (
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                      </span>
                    )}
                  </div>
                  <p
                    className={`text-sm font-bold ${isProfitable ? "text-green-400" : "text-red-400"} ${!closed && isSubscribed && liveMarkPrice ? "transition-all duration-150" : ""}`}
                  >
                    {pnl !== null && pnl !== undefined && !isClosedExternally
                      ? `$${pnl.toFixed(2)}`
                      : "---"}
                  </p>
                  {pnlPercentage !== null && pnlPercentage !== undefined && (
                    <p
                      className={`text-xs ${isProfitable ? "text-green-400" : "text-red-400"}`}
                    >
                      (
                      {isClosedExternally
                        ? "---"
                        : typeof pnlPercentage === "number"
                          ? pnlPercentage.toFixed(2)
                          : parseFloat(pnlPercentage.toString()).toFixed(2)}
                      %)
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-xs text-text-secondary mb-1">Exchange</p>
                  <p className="text-sm text-text-primary capitalize">
                    {position.exchangeName || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-text-secondary mb-1">Opened At</p>
                  <p className="text-xs  text-text-primary">
                    {formatDateTime(position.openedAt)}
                  </p>
                </div>
                {closed && "closedAt" in position && (
                  <div>
                    <p className="text-xs text-text-secondary mb-1">
                      Closed At
                    </p>
                    <p className="text-xs  text-text-primary">
                      {formatDateTime(position.closedAt)}
                    </p>
                  </div>
                )}
              </div>

              {/* Price Chart */}
              <div>
                {isLiveChart ? (
                  <>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-bold text-text-primary">
                        Price Chart
                      </h3>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <span className="text-xs text-text-secondary">
                          Live Chart
                        </span>
                        <div className="relative inline-block w-10 h-5">
                          <input
                            type="checkbox"
                            checked={isLiveChart}
                            onChange={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setIsLiveChart(e.target.checked);
                              if (scrollContainerRef.current) {
                                scrollPositionRef.current =
                                  scrollContainerRef.current.scrollTop;
                              }
                            }}
                            className="sr-only"
                          />
                          <div
                            className={`block w-10 h-5 rounded-full transition-colors ${
                              isLiveChart ? "bg-accent" : "bg-gray-600"
                            }`}
                          />
                          <div
                            className={`absolute left-1 top-1 bg-white w-3 h-3 rounded-full transition-transform ${
                              isLiveChart ? "translate-x-5" : "translate-x-0"
                            }`}
                          />
                        </div>
                      </label>
                    </div>
                    <TradingViewChartWidget symbol={position.symbol} />
                  </>
                ) : (
                  <PositionChart
                    priceChanges={priceChanges}
                    entryPrice={parseFloat(position.entryPrice.toString())}
                    exitPrice={
                      closed &&
                      "exitPrice" in position &&
                      position.exitPrice
                        ? parseFloat(position.exitPrice.toString())
                        : null
                    }
                    isProfitable={isProfitable}
                    openedAt={position.openedAt}
                    closedAt={
                      closed && "closedAt" in position
                        ? position.closedAt
                        : undefined
                    }
                    onRequestOlderCandles={loadOlderCandles}
                    onRequestNewerCandles={loadNewerCandles}
                    hasMoreHistory={hasMoreHistory}
                    hasMoreFuture={hasMoreFuture}
                    onFollowLiveChange={setFollowLive}
                    viewportResetKey={`${selectedInterval}-${positionIdentifier}`}
                    enabledIntervals={enabledIntervals}
                    selectedInterval={selectedInterval}
                    onIntervalChange={setSelectedInterval}
                    onIntervalClick={() => {
                      if (scrollContainerRef.current) {
                        scrollPositionRef.current =
                          scrollContainerRef.current.scrollTop;
                      }
                    }}
                    isLoading={isLoading}
                    isLoadingMore={isLoadingMore}
                    error={candleError}
                    emptyMessage="No price data available"
                    headerActions={
                      <label className="flex items-center gap-2 cursor-pointer">
                        <span className="text-xs text-text-secondary">
                          Live Chart
                        </span>
                        <div className="relative inline-block w-10 h-5">
                          <input
                            type="checkbox"
                            checked={isLiveChart}
                            onChange={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setIsLiveChart(e.target.checked);
                              if (scrollContainerRef.current) {
                                scrollPositionRef.current =
                                  scrollContainerRef.current.scrollTop;
                              }
                            }}
                            className="sr-only"
                          />
                          <div
                            className={`block w-10 h-5 rounded-full transition-colors ${
                              isLiveChart ? "bg-accent" : "bg-gray-600"
                            }`}
                          />
                          <div
                            className={`absolute left-1 top-1 bg-white w-3 h-3 rounded-full transition-transform ${
                              isLiveChart ? "translate-x-5" : "translate-x-0"
                            }`}
                          />
                        </div>
                      </label>
                    }
                  />
                )}
              </div>
              <div className="rounded-xl bg-black/30 p-4 border border-gray-500/20 space-y-4">
                <div>
                  <p className="text-xs uppercase tracking-wide text-text-secondary">
                    Share your trade
                  </p>
                  <p className="text-sm text-text-secondary mt-1">
                    Share your PNL card to invite your friends to try Trigger
                    and earn tokens.
                  </p>
                </div>
                <PnlCardShare
                  position={positionForCard || position}
                  userDisplayName={userDisplayName}
                  referralLink={referralLink}
                />
              </div>
            </div>
          </div>
          {showCloseAction && (
            <div className="sticky bottom-0 bg-[#1a1b20] border-t border-gray-500/30 p-4">
              {!closed && !closeSuccess && !isClosedExternally && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setCloseError(null);
                      closePositionMutation.mutate();
                    }}
                    disabled={isCloseButtonDisabled}
                    className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-red-500 border border-gray-500/30 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isCloseButtonDisabled && (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    )}
                    {isCloseButtonDisabled ? "Closing..." : "Close Position"}
                  </button>
                  {closeError && (
                    <p className="mt-2 text-xs text-red-400 text-center">
                      {closeError}
                    </p>
                  )}
                </>
              )}
              {closed && closeSuccess && !isClosedExternally && (
                <p className="text-center text-sm font-semibold text-green-400">
                  Position closed successfully
                </p>
              )}
              {/* Closed Externally Warning */}
              {isClosedExternally && (
                <div className="px-3 py-3 bg-yellow-500/10 border border-yellow-500/30 rounded-md mb-4">
                  <p className="text-xs text-yellow-400">
                    ⚠️ Position closed externally - we will update information about it shortly
                  </p>
                </div>
              )}
            </div>
          )}
        </Drawer.Content>
      </Drawer.Portal>
    </DrawerRoot>
  );
};
